import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { Button } from '@frontend/shadcn/components/ui/button';
import { Input } from '@frontend/shadcn/components/ui/input';
import { MagnifyingGlassIcon } from '@revfluence/fresh-icons/regular/esm';
import { GroupIcon, PersonIcon, StarIcon } from '@components';
import { Spinner } from '../../Analytics/components/ui/spinner';
import { SearchQueryFormData } from '../hooks/use-filters-form';
import { CreatorInput } from './creator-input';
import { QueryVariables, SearchContext } from '../types';
import type { CategorizedLoadingStates } from '../hooks/use-loading-states';

interface SearchFormProps {
  form: UseFormReturn<SearchQueryFormData>;
  onSubmit: (e: React.FormEvent) => void;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearchContextSubmit: (searchContext: SearchContext, query: string) => void;
  onContextChange?: (searchContext: SearchContext | null) => void;
  fetching?: boolean;
  searchContext: SearchContext | null;
  setSearchContext: (value: SearchContext | null) => void;
  setSearchContextAndClearQuery: (value: SearchContext | null) => void;
  updateUrlParams: (params: Partial<QueryVariables>) => void;
  handleSearch: () => void;
  queryVariables: QueryVariables;
  loadingStates: CategorizedLoadingStates;
}

// AI typing animation component
const TypingAnimation = ({ className = '' }: { className?: string }) => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev === '...') return '';
        return `${prev}.`;
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <span className={`inline-block min-w-[24px] ${className}`}>
      {dots}
    </span>
  );
};

// AI-style loading overlay for input
const AILoadingOverlay = ({ message }: { message: string }) => (
  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-start pl-4 rounded-lg">
    <div className="flex items-center space-x-2 text-blue-600">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]" />
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" />
      </div>
      <span className="text-sm font-medium">{message}</span>
      <TypingAnimation className="text-blue-600 font-bold" />
    </div>
  </div>
);

export const SearchForm: React.FC<SearchFormProps> = ({
  form,
  onSubmit: _onSubmit,
  onSearchChange,
  onSearchContextSubmit,
  onContextChange,
  fetching,
  searchContext,
  setSearchContext,
  setSearchContextAndClearQuery,
  updateUrlParams,
  handleSearch,
  queryVariables,
  loadingStates,
}) => {
  // Extract loading states
  const {
    shouldShowFiltersLoading,
    shouldDisableInteractions,
  } = loadingStates.uiStates;

  const handleButtonClick = (buttonType: SearchContext) => {
    // If clicking the same button, deselect it (preserve query)
    if (searchContext === buttonType) {
      setSearchContext(null);
      onContextChange?.(null);
    } else {
      // Otherwise, select the new button and clear the query
      setSearchContextAndClearQuery(buttonType);
      onContextChange?.(buttonType);
    }
  };

  const getAspireAiGradient = () => {
    if (searchContext === 'aspire-ai' && shouldShowFiltersLoading) {
      return 'bg-gradient-to-r from-[#F6C947] via-[#55A2DF] to-[#6C77DE] animate-pulse';
    }
    return searchContext === 'aspire-ai'
      ? 'bg-gradient-to-r from-[#F6C947] via-[#55A2DF] to-[#6C77DE]'
      : 'bg-gradient-to-r from-[#F6C947]/30 via-[#55A2DF]/30 to-[#6C77DE]/30';
  };

  const getButtonStyles = (buttonType: SearchContext) => {
    const isActive = searchContext === buttonType;
    const isLoading = buttonType === 'aspire-ai' && shouldShowFiltersLoading;

    if (isActive) {
      // Active styles with loading state
      return `rounded-lg border border-[#16282D] bg-[#F9FAFB] hover:bg-gray-100 flex items-center ${
        isLoading ? 'opacity-75' : ''
      }`;
    } else {
      // Inactive styles
      return 'rounded-lg border border-[#E5E8EB] bg-white hover:bg-gray-50 flex items-center';
    }
  };

  const getPlaceholderText = () => {
    if (searchContext === 'aspire-ai' && shouldShowFiltersLoading) {
      return 'AI is processing your request...';
    }

    switch (searchContext) {
      case 'aspire-ai':
        return 'Search for a creator like: \'Vegan beauty influencers with high engagement\'';
      case 'username':
        return 'Search for a creator with their exact username';
      case 'similar-creators':
        return 'Enter creator usernames (comma-separated, up to 5): e.g. username1, username2';
      default:
        return 'Select a search type above to get started';
    }
  };

  const handleSearchSubmit = (): void => {
    if (searchContext === 'aspire-ai' || searchContext === 'username') {
      if (searchContext === 'aspire-ai') {
        const prompt = form.watch('prompt');
        if (!prompt?.trim()) return;
        // Use new context-specific submission for aspire-ai with prompt
        onSearchContextSubmit(searchContext, prompt);
      } else if (searchContext === 'username') {
        const query = form.watch('username');
        if (!query?.trim()) return;
        // Use new context-specific submission for username with query
        onSearchContextSubmit(searchContext, query);
      }
    } else if (searchContext === 'similar-creators') {
      const creators = form.watch('similarToCreators');
      if (!creators || creators.length === 0) return;

      // For similar creators, pass the array as a comma-separated string
      const creatorsString = creators.join(',');
      onSearchContextSubmit(searchContext, creatorsString);
    }
  };

  const queryInput = (
    <div className="relative">
      <Input
        type="text"
        placeholder={getPlaceholderText()}
        value={form.watch('prompt')}
        onChange={onSearchChange}
        className={`w-full border-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0 ${
          shouldShowFiltersLoading ? 'bg-blue-50/50' : ''
        }`}
        aria-label="Search creators"
        disabled={!searchContext || shouldShowFiltersLoading}
      />
      {shouldShowFiltersLoading && (
        <AILoadingOverlay message="Generating AI suggestions" />
      )}
    </div>
  );

  const usernameInput = (
    <Input
      type="text"
      placeholder="Search for a creator with their exact username"
      value={form.watch('username')}
      onChange={onSearchChange}
      className="w-full border-none shadow-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-0"
      aria-label="Search creators"
      disabled={!searchContext}
    />
  );

  const similarCreatorsInput = (
    <CreatorInput
      form={form}
      placeholder="Enter a creator username to find similar creators (up to 5 creators)"
      disabled={!searchContext}
      maxItems={5}
      updateUrlParams={updateUrlParams}
      handleSearch={handleSearch}
      queryVariables={queryVariables}
    />
  );

  // Check if search can be performed
  const canSearch = useCallback(() => {
    if (!searchContext) return false;

    if (searchContext === 'aspire-ai') {
      const prompt = form.watch('prompt');
      return prompt && prompt.trim().length > 0;
    } else if (searchContext === 'username') {
      const query = form.watch('username');
      return query && query.trim().length > 0;
    } else if (searchContext === 'similar-creators') {
      const creators = form.watch('similarToCreators');
      return creators && creators.length > 0;
    }

    return false;
  }, [searchContext, form]);

  const isSearchDisabled = useMemo(() => {
    return shouldDisableInteractions ||
            shouldShowFiltersLoading ||
            !canSearch() ||
            Object.keys(form.formState.errors).length > 0;
  }, [shouldDisableInteractions, shouldShowFiltersLoading, canSearch, form.formState.errors]);

  // Handle form submission - only submit if the current search context has valid data
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Only submit if we have an active search context and can search
    if (searchContext && canSearch() && !shouldShowFiltersLoading) {
      handleSearchSubmit();
    }
  };

  return (
    <div className="flex p-3 flex-col items-start gap-2 self-stretch rounded-2xl border border-[#E5E8EB] bg-white">
      <form onSubmit={handleFormSubmit} id="search-form" className="w-full">
        {/* First row - Search bar */}
        <div className="w-full">
          {searchContext === 'aspire-ai' && queryInput}
          {searchContext === 'username' && usernameInput}
          {searchContext === 'similar-creators' && similarCreatorsInput}
        </div>

        {/* Second row - Three buttons aligned left and search button aligned right */}
        <div className="flex items-center justify-between mt-2">{/* mt-2 = 8px spacing */}
          <div className="flex items-center gap-2">
            <div className={`p-[2px] rounded-lg ${getAspireAiGradient()}`}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={`${getButtonStyles('aspire-ai')} border-0`}
                onClick={() => handleButtonClick('aspire-ai')}
                aria-pressed={searchContext === 'aspire-ai'}
                disabled={shouldDisableInteractions}
              >
                <StarIcon size={14} fill="#16282D" className="mr-2" />
                <span>Aspire AI</span>
                {searchContext === 'aspire-ai' && shouldShowFiltersLoading && (
                  <div className="ml-2 flex space-x-1">
                    <div className="w-1 h-1 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.3s]" />
                    <div className="w-1 h-1 bg-blue-600 rounded-full animate-bounce [animation-delay:-0.15s]" />
                    <div className="w-1 h-1 bg-blue-600 rounded-full animate-bounce" />
                  </div>
                )}
              </Button>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className={getButtonStyles('username')}
              onClick={() => handleButtonClick('username')}
              aria-pressed={searchContext === 'username'}
              disabled={shouldDisableInteractions}
            >
              <PersonIcon size={14} fill="#16282D" className="mr-2" />
              Username
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className={getButtonStyles('similar-creators')}
              onClick={() => handleButtonClick('similar-creators')}
              aria-pressed={searchContext === 'similar-creators'}
              disabled={shouldDisableInteractions}
            >
              <GroupIcon size={14} fill="#16282D" className="mr-2" />
              Similar Creators
            </Button>
          </div>

          {/* Search button aligned to the right */}
          <button
            type="button"
            onClick={handleSearchSubmit}
            className={`flex w-8 h-8 px-2 py-1 justify-center items-center rounded-lg bg-[#16282D] hover:bg-[#0f1f24] disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all duration-200 ${
              shouldShowFiltersLoading ? 'animate-pulse' : ''
            }`}
            aria-label={shouldShowFiltersLoading ? 'AI is processing...' : 'Search'}
            disabled={isSearchDisabled}
          >
            {shouldShowFiltersLoading ? (
              <div className="flex space-x-1">
                <div className="w-1 h-1 bg-white rounded-full animate-bounce [animation-delay:-0.3s]" />
                <div className="w-1 h-1 bg-white rounded-full animate-bounce [animation-delay:-0.15s]" />
                <div className="w-1 h-1 bg-white rounded-full animate-bounce" />
              </div>
            ) : fetching ? (
              <Spinner size="sm" spinning className="text-white" />
            ) : (
              <MagnifyingGlassIcon className="h-4 w-4 text-white" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
